import React, { useEffect, useRef } from 'react';
import { Router } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Bus,
  Fuse,
  NotificationManager,
  printMissingConfigError,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useUserMessages,
} from '@atlas/core';
import { Loader } from '@atlas/ui';

import MultiPopupManager from 'common/Notifications/Popups/MultiPopup/MultiPopupManager';
import Cookies from 'components/Cookies/Cookies';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import GTMInitializer from 'components/GTMInitializer';
import history from 'components/Layout/history';
import LayoutErrorFallback from 'components/Layout/LayoutErrorFallback';
import QueryRoutes from 'components/Layout/QueryRoutes';
import LiveChatView from 'components/LiveChat/LiveChatView';
import OneButtonCookies from 'components/OneButtonCookies/OneButtonCookies';
import addBetBoosterWindowFunctions from 'components/Sportsbook/BetBooster/addBetBoosterWindowFunctions';
import { BetBoosterGlobalContextProvider } from 'components/Sportsbook/BetBooster/BetBoosterGlobalContext';
import { useShowPopupBonuses } from 'hooks/useBonuses';
import { useShowPopupSignUpDelay } from 'hooks/useShowPopupSignUpDelay';
import useThemeBootstrap from 'hooks/useThemeBootstrap';
import { COOKIES_ACCEPTED_EVENT } from 'src/common/constants';
import { SmarticoProvider } from 'src/components/ClientProfile/Achievements/Smartico';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import EmailVerificationBanner from 'src/components/EmailVerificationBanner/EmailVerificationBanner';
import useDepositFailedNotification from 'src/hooks/useDepositFailedNotification';
import { useHeaderOffset } from 'src/hooks/useHeaderOffset';
import useRedirectAfterDeposit from 'src/hooks/useRedirectAfterDeposit';

import Header from './Header';

const AuthorizationLoader = ({ children }) => {
  const { resolved } = useApplicationState();

  if (resolved) {
    return children;
  }

  return <Loader className="page__main" />;
};

AuthorizationLoader.propTypes = {
  children: PropTypes.node,
};

const RootLayout = ({ children }) => {
  const headerRef = useRef(null);
  const stickyHeaderWrapperRef = useHeaderOffset();
  const { getUnreadMessages } = useUserMessages();
  const { account, authenticated } = useApplicationState();
  const accountId = account?.id ?? null;
  const cookiesType = SiteConfigManager.getConfig('config.config.general.cookiesType', false);
  const enableCookiesPopup = SiteConfigManager.getConfig(
    'config.config.general.enableCookiesPopup',
    true,
  );
  const pages = SiteConfigManager.getConfig('config.pages', []);

  if (pages.length === 0) {
    printMissingConfigError('config.pages');
  }

  useEffect(() => {
    // TODO: Should be reworked and connected to scheduler
    const readyHandler = () => {
      if (
        SiteConfigManager.getConfig('config.config.general.enableDemoPopup') &&
        !StorageManager.getItem('demoPopupAcceptedKey')
      ) {
        NotificationManager.display({
          type: 'demo',
        });
      }

      document.removeEventListener('readystatechange', readyHandler);
    };

    document.addEventListener('readystatechange', readyHandler);

    return () => {
      document.removeEventListener('readystatechange', readyHandler);
    };
  }, []);

  const identifyHotjarUser = React.useCallback(() => {
    if (typeof window.hj !== 'function') {
      return;
    }

    if (!authenticated) {
      window.hj('identify', null, { client_id: null });
      return;
    }

    if (accountId) {
      window.hj('identify', accountId, { client_id: accountId });
    }
  }, [accountId, authenticated]);

  useEffect(() => {
    identifyHotjarUser();
  }, [identifyHotjarUser]);

  useEffect(() => {
    Bus.on(COOKIES_ACCEPTED_EVENT, identifyHotjarUser);

    return () => {
      Bus.off(COOKIES_ACCEPTED_EVENT, identifyHotjarUser);
    };
  }, [identifyHotjarUser]);

  addBetBoosterWindowFunctions();
  useThemeBootstrap();
  useShowPopupSignUpDelay();
  useShowPopupBonuses();
  useRedirectAfterDeposit();
  useDepositFailedNotification();

  useEffect(() => {
    if (authenticated) {
      getUnreadMessages();
    }
  }, [authenticated, getUnreadMessages]);

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <Router history={history}>
        <SmarticoProvider>
          <BetBoosterGlobalContextProvider>
            <CustomStylesPortal />

            <div className="sticky-header-wrapper" ref={stickyHeaderWrapperRef}>
              <EmailVerificationBanner />
              <Header ref={headerRef} />
            </div>

            <main className="page">
              <AuthorizationLoader>{children}</AuthorizationLoader>
              <DebugInfo />
            </main>

            <QueryRoutes />
            <LiveChatView />

            {enableCookiesPopup && cookiesType === 'Decline+Accept' && <Cookies />}
            {enableCookiesPopup && cookiesType === 'Ok' && <OneButtonCookies />}
            {!enableCookiesPopup && <GTMInitializer />}
            <MultiPopupManager />
          </BetBoosterGlobalContextProvider>
        </SmarticoProvider>
      </Router>
    </Fuse>
  );
};

RootLayout.propTypes = {
  children: PropTypes.node,
};

export default RootLayout;
